import { useState, useRef, useEffect } from 'react';
import { useBusinessContextStore } from '../store/businessContextStore';
import { useBusinesses } from '../hooks/useBusinesses';

export function BusinessSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const { businesses, isLoading, error } = useBusinesses();
  const { selectedBusinessId, selectedBusinessName, setSelectedBusiness } = useBusinessContextStore();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectBusiness = (business: { id: string; name: string }) => {
    setSelectedBusiness(business.id, business.name);
    setIsOpen(false);
  };

  const displayText = selectedBusinessName || 'Select Business';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium
                   transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500
                   ${selectedBusinessId 
                     ? 'bg-primary-50 text-primary-700 hover:bg-primary-100' 
                     : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                   }`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {/* Building icon */}
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
        
        <span className="max-w-32 truncate">{displayText}</span>
        
        {/* Dropdown arrow */}
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg 
                     border border-secondary-200 py-1 z-50"
          role="listbox"
        >
          {/* Header */}
          <div className="px-3 py-2 border-b border-secondary-100">
            <p className="text-xs font-medium text-secondary-500 uppercase tracking-wide">
              Operating As
            </p>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="px-3 py-4 text-center">
              <svg
                className="animate-spin h-5 w-5 text-primary-600 mx-auto"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="text-sm text-secondary-500 mt-2">Loading businesses...</p>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Business List */}
          {!isLoading && !error && (
            <div className="max-h-64 overflow-y-auto">
              {businesses.length === 0 ? (
                <div className="px-3 py-4 text-center">
                  <p className="text-sm text-secondary-500">No businesses found</p>
                </div>
              ) : (
                businesses.map((business) => (
                  <button
                    key={business.id}
                    onClick={() => handleSelectBusiness(business)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm
                               transition-colors hover:bg-secondary-50
                               ${selectedBusinessId === business.id 
                                 ? 'bg-primary-50 text-primary-700' 
                                 : 'text-secondary-700'
                               }`}
                    role="option"
                    aria-selected={selectedBusinessId === business.id}
                  >
                    {/* Checkmark for selected */}
                    <span className="w-4 h-4 flex items-center justify-center">
                      {selectedBusinessId === business.id && (
                        <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </span>
                    <span className="truncate">{business.name}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}